import { useState, useEffect, useMemo, useRef } from "react";
import { slotsApi } from "../../../api";
import classes from "./BookAppointment.module.scss";

type AppointmentType = "New Client" | "Follow-Up";

type AvailableSlot = {
  id: string; date: string; time: string; timeVal: string; duration: number;
};

type BookAppointmentProps = {
  onClose?: () => void;
  isActive: boolean; // true if client has at least one completed appointment → Follow-Up
};

function formatConfirmDate(iso: string) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric" });
}
function displayDate(iso: string) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

// Steps: 1=Pick Date, 2=Pick Time, 3=Confirmed
type Step = 1 | 2 | 3;

const BookAppointment = ({ onClose, isActive }: BookAppointmentProps) => {
  const aptType: AppointmentType = isActive ? "Follow-Up" : "New Client";
  const requiredDuration          = isActive ? 30 : 45;

  const [step,         setStep]         = useState<Step>(1);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);

  const [slots,   setSlots]   = useState<AvailableSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [error,   setError]   = useState("");

  useEffect(() => {
    slotsApi.available()
      .then(d => setSlots(d.slots ?? []))
      .catch(() => setError("Could not load available slots."))
      .finally(() => setLoading(false));
  }, []);

  const slotsByDate = useMemo(() => {
    const map: Record<string, AvailableSlot[]> = {};
    slots
      .filter(s => s.duration === requiredDuration)
      .forEach(s => {
        if (!map[s.date]) map[s.date] = [];
        map[s.date].push(s);
      });
    return map;
  }, [slots, requiredDuration]);

  const dates = Object.keys(slotsByDate).sort();
  const timesForDate = selectedDate ? (slotsByDate[selectedDate] ?? []) : [];

  const handleConfirm = async () => {
    if (!selectedSlot) return;
    setBooking(true);
    setError("");
    try {
      await slotsApi.book(selectedSlot.id, aptType);
      setStep(3);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Booking failed. Please try again.");
    } finally {
      setBooking(false);
    }
  };

  const dateSliderRef = useRef<HTMLDivElement>(null);
  const timeSliderRef = useRef<HTMLDivElement>(null);

  const scroll = (ref: { current: HTMLDivElement | null }, dir: "left" | "right") => {
    if (ref.current) ref.current.scrollBy({ left: dir === "left" ? -200 : 200, behavior: "smooth" });
  };

  return (
    <div className={classes.page}>
      {/* Header */}
      <div className={classes.header}>
        <h1 className={classes.title}>Book an Appointment</h1>
        <p className={classes.subtitle}>Schedule a session with RD. Tatiana Karam.</p>

        {step < 3 && (
          <div className={classes.steps}>
            <div className={`${classes.step_item} ${step >= 1 ? classes.step_active : ""}`}>
              <div className={`${classes.step_circle} ${step >= 1 ? classes.step_circle_active : ""}`}>1</div>
              <span className={`${classes.step_label} ${step >= 1 ? classes.step_label_active : ""}`}>Date</span>
            </div>
            <div className={`${classes.step_line} ${step >= 2 ? classes.step_line_active : ""}`} />
            <div className={`${classes.step_item} ${step >= 2 ? classes.step_active : ""}`}>
              <div className={`${classes.step_circle} ${step >= 2 ? classes.step_circle_active : ""}`}>2</div>
              <span className={`${classes.step_label} ${step >= 2 ? classes.step_label_active : ""}`}>Time</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Step 1: Pick a Date ── */}
      {step === 1 && (
        <div className={classes.card}>
          <div className={classes.card_inner}>
            <div className={classes.card_left}>
              <span className={classes.step_badge}>STEP 1</span>
              <h2 className={classes.card_title}>Choose a Date</h2>
              <p className={classes.card_desc}>Select from available days.</p>
              <div style={{ marginTop: 12, padding: "8px 12px", background: "#EBF7EB", borderRadius: 8, fontSize: 13, color: "#4A7C40" }}>
                {aptType} · {requiredDuration} min
              </div>
            </div>
          </div>

          {loading ? (
            <div style={{ padding: "32px 0", textAlign: "center", color: "#999" }}>
              <div style={{ width: 28, height: 28, border: "3px solid #EBF7EB", borderTopColor: "#4A7C40", borderRadius: "50%", animation: "spin 0.7s linear infinite", margin: "0 auto 12px" }} />
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
              Loading available dates…
            </div>
          ) : dates.length === 0 ? (
            <div style={{ padding: "32px 0", textAlign: "center", color: "#999" }}>
              <p style={{ fontSize: 15 }}>No available slots right now.</p>
              <p style={{ fontSize: 13, marginTop: 6 }}>Check back soon or ask your dietitian to open more slots.</p>
            </div>
          ) : (
            <div className={classes.slider_wrap}>
              <button type="button" className={classes.slider_arrow} onClick={() => scroll(dateSliderRef, "left")}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <div className={classes.slider_track} ref={dateSliderRef}>
                {dates.map(date => (
                  <button key={date} type="button"
                    className={`${classes.date_card} ${selectedDate === date ? classes.date_card_selected : ""}`}
                    onClick={() => { setSelectedDate(date); setSelectedSlot(null); setStep(2); }}
                  >
                    <span className={classes.date_weekday}>
                      {new Date(...(date.split("-").map(Number) as [number,number,number])).toLocaleDateString("en-US", { weekday: "short" })}
                    </span>
                    <span className={classes.date_day}>
                      {new Date(...(date.split("-").map(Number) as [number,number,number])).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                    <span className={classes.date_slots}>{slotsByDate[date].length}</span>
                  </button>
                ))}
              </div>
              <button type="button" className={classes.slider_arrow} onClick={() => scroll(dateSliderRef, "right")}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>
          )}

          {error && <p style={{ color: "#C0392B", fontSize: 13, margin: "8px 0" }}>{error}</p>}
        </div>
      )}

      {/* ── Step 2: Pick a Time ── */}
      {step === 2 && selectedDate && (
        <div className={classes.card}>
          <div className={classes.card_inner}>
            <div className={classes.card_left}>
              <span className={classes.step_badge}>STEP 2</span>
              <h2 className={classes.card_title}>Choose a Time</h2>
              <p className={classes.card_desc}>{displayDate(selectedDate)}</p>
            </div>
            <button type="button" className={classes.back_btn} onClick={() => { setStep(1); setSelectedSlot(null); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              Back
            </button>
          </div>

          <div className={classes.slider_wrap}>
            <button type="button" className={classes.slider_arrow} onClick={() => scroll(timeSliderRef, "left")}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            <div className={classes.slider_track} ref={timeSliderRef}>
              {timesForDate.map(slot => (
                <button key={slot.id} type="button"
                  className={`${classes.time_btn} ${selectedSlot?.id === slot.id ? classes.time_btn_selected : ""}`}
                  onClick={() => setSelectedSlot(slot)}
                >
                  <span className={classes.time_val}>{slot.time}</span>
                  <span className={classes.time_dur}>{slot.duration} min</span>
                </button>
              ))}
            </div>
            <button type="button" className={classes.slider_arrow} onClick={() => scroll(timeSliderRef, "right")}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>

          {error && <p style={{ color: "#C0392B", fontSize: 13, margin: "8px 0" }}>{error}</p>}

          <button type="button" className={classes.primary_btn}
            onClick={handleConfirm}
            disabled={!selectedSlot || booking}
          >
            {booking ? (
              <span style={{ display: "inline-block", width: 16, height: 16, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
            ) : "Confirm Appointment"}
          </button>
        </div>
      )}

      {/* ── Step 3: Confirmed ── */}
      {step === 3 && selectedSlot && (
        <div className={classes.confirm_card}>
          <div className={classes.confirm_check}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <div className={classes.confirm_text}>
            <h2 className={classes.confirm_title}>Appointment Confirmed!</h2>
            <p className={classes.confirm_subtitle}>Your appointment has been successfully booked.</p>
          </div>

          <div className={classes.confirm_details}>
            <div className={classes.confirm_detail}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
              </svg>
              <div>
                <span className={classes.detail_label}>Type</span>
                <span className={classes.detail_value}>{aptType}</span>
              </div>
            </div>
            <div className={classes.confirm_divider} />
            <div className={classes.confirm_detail}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              <div>
                <span className={classes.detail_label}>Date</span>
                <span className={classes.detail_value}>{formatConfirmDate(selectedSlot.date)}</span>
              </div>
            </div>
            <div className={classes.confirm_divider} />
            <div className={classes.confirm_detail}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              <div>
                <span className={classes.detail_label}>Time</span>
                <span className={classes.detail_value}>{selectedSlot.time} · {selectedSlot.duration} min</span>
              </div>
            </div>
          </div>

          <div className={classes.confirm_actions}>
            <button type="button" className={classes.primary_btn} onClick={onClose}>View My Appointments</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookAppointment;
